import { useId } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAnnounce } from "@/hooks/use-announcer";
import {
  type Contrast,
  FONT_SCALE_LABELS,
  type FontScale,
  type Motion,
  type Theme,
  useAppearance,
} from "@/hooks/use-appearance";

interface SettingRowProps<T extends string> {
  label: string;
  description: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}

function SettingRow<T extends string>({
  label,
  description,
  value,
  onChange,
  options,
}: SettingRowProps<T>) {
  const id = useId();
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-4">
      <div className="flex flex-col gap-0.5">
        {/* A Select trigger is a <button>, which does not reliably take its name
            from a label's htmlFor — hence the explicit aria-labelledby. */}
        <span id={id} className="text-sm font-medium">
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      <Select value={value} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger className="w-40" aria-labelledby={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "Match system" },
];

const CONTRAST_OPTIONS: { value: Contrast; label: string }[] = [
  { value: "normal", label: "Standard" },
  { value: "more", label: "High" },
  { value: "system", label: "Match system" },
];

const MOTION_OPTIONS: { value: Motion; label: string }[] = [
  { value: "full", label: "Full" },
  { value: "reduced", label: "Reduced" },
  { value: "system", label: "Match system" },
];

const FONT_SCALE_OPTIONS = (Object.keys(FONT_SCALE_LABELS) as FontScale[]).map(
  (value) => ({ value, label: FONT_SCALE_LABELS[value] }),
);

/**
 * Replaces the single cycling "Theme: Light" menu item, which took three
 * open-click-reopen rounds just to discover that "System" existed — and whose
 * label described its current state rather than what activating it would do.
 */
export function AppearanceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    theme,
    setTheme,
    contrast,
    setContrast,
    motion,
    setMotion,
    fontScale,
    setFontScale,
  } = useAppearance();
  const announce = useAnnounce();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Appearance &amp; accessibility</DialogTitle>
          <DialogDescription>
            These settings are saved in this browser.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <SettingRow
            label="Theme"
            description="Light or dark colours."
            value={theme}
            onChange={(v) => {
              setTheme(v);
              announce(`Theme: ${v}`);
            }}
            options={THEME_OPTIONS}
          />
          <SettingRow
            label="Contrast"
            description="High contrast strengthens text and borders."
            value={contrast}
            onChange={(v) => {
              setContrast(v);
              announce(`Contrast: ${v}`);
            }}
            options={CONTRAST_OPTIONS}
          />
          <SettingRow
            label="Text size"
            description="Scales the whole interface, not just the text."
            value={fontScale}
            onChange={(v) => {
              setFontScale(v);
              announce(`Text size: ${FONT_SCALE_LABELS[v]}`);
            }}
            options={FONT_SCALE_OPTIONS}
          />
          <SettingRow
            label="Motion"
            description="Reduced motion removes animations and smooth scrolling."
            value={motion}
            onChange={(v) => {
              setMotion(v);
              announce(`Motion: ${v}`);
            }}
            options={MOTION_OPTIONS}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
